import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, DestroyRef, HostBinding, inject, Input, input, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, takeUntil, timer } from 'rxjs';

@Component({
  selector: 'coin-info-tooltip',
  templateUrl: './info-tooltip.component.html',
  styleUrls: ['./info-tooltip.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InfoTooltipComponent {
  @Input() public html: string;
  @Input() public text: string;
  @Input() public template: TemplateRef<unknown>;
  @Input() public size = '1.13rem';
  @Input() public color: string;
  @Input() public position: 'left' | 'center' = 'center';
  /** Lets the pointer enter the overlay, so links and other controls inside it stay usable. */
  public readonly interactive = input(false);
  /** `card` drops the dark tooltip styling so projected content can bring its own layout. */
  public readonly variant = input<'tooltip' | 'card'>('tooltip');

  private readonly destroyRef = inject(DestroyRef);
  private readonly cancelHide$ = new Subject<void>();

  @HostBinding('style') public get style(): Record<string, string> {
    return {
      width: this.size,
      height: this.size,
      fontSize: this.size,
      lineHeight: this.size,
      color: this.color
    };
  }

  // Each position lists the flipped variant as fallback, so the overlay stays inside the viewport near a bottom edge.
  private readonly overlayPositions: { [key: string]: ConnectedPosition[] } = {
    center: [
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' }
    ],
    left: [
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
    ]
  };

  public get overlayPosition(): ConnectedPosition[] {
    return this.overlayPositions[this.position];
  }

  public isVisible = false;

  public show(): void {
    this.cancelHide$.next();
    this.isVisible = true;
  }

  public hide(): void {
    if (!this.interactive()) {
      this.isVisible = false;
      return;
    }

    // Keeps the overlay alive while the pointer travels from the icon onto it.
    timer(HIDE_DELAY_IN_MS)
      .pipe(takeUntil(this.cancelHide$), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isVisible = false;
      });
  }
}

const HIDE_DELAY_IN_MS = 150;
